/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { makeKeyMapWithCommon, ToolTipContent } from '@atlaskit/editor-common/keymaps';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { token } from '@atlaskit/tokens';

import { renderChevronLeftIcon, renderChevronRightIcon, renderFullscreenEnterIcon } from './icons';
import { messages } from './messages';

// Keyboard shortcuts for step-through navigation, surfaced as tooltip hints
// (↑ / ↓). The actual key handling lives in `useReviewKeyboardShortcuts`
// (hooks.ts) — keep the bindings in sync.
const previousChangeKeymap = makeKeyMapWithCommon('Go to previous', 'ArrowUp');
const nextChangeKeymap = makeKeyMapWithCommon('Go to next', 'ArrowDown');

// [CCI-17296] Inline step-through navigation cluster — previous / "N / N"
// counter / next — shown to the left of the action icons when the user is
// stepping through more than one change.
const navigationTrayCSS = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
});

const segmentCounterCSS = css({
	font: token('font.body.small'),
	color: token('color.text.subtle'),
	whiteSpace: 'nowrap',
	textAlign: 'center',
	minWidth: token('space.300'),
});

const actionButtonsCSS = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
});

export interface ActionButtonsProps {
	canGoNext: boolean;
	canGoPrevious: boolean;
	isStepThroughExpanded: boolean;
	onNext: () => void;
	onPrevious: () => void;
	onToggleStepThrough: () => void;
	segmentIndex: number;
	totalSegments: number;
}

/**
 * The "Step through all changes" cluster for the Post Stream Review toolbar.
 *
 * Renders a single "Step through all changes" toggle button. When expanded it
 * reveals previous/next navigation arrows and a "current / total" counter.
 * When there is only a single segment nothing actionable is rendered.
 */
export function ActionButtons({
	totalSegments,
	segmentIndex,
	canGoPrevious,
	canGoNext,
	isStepThroughExpanded,
	onPrevious,
	onNext,
	onToggleStepThrough,
}: ActionButtonsProps): JSX.Element | null {
	const intl = useIntl();

	// Custom tooltip content appending the keyboard shortcut to the nav labels
	// (e.g. "Go to previous ↑"). Memoized so the `tooltip` prop object is stable
	// across renders (`@atlassian/perf-linting/no-unstable-inline-props`). Declared
	// before the early return below to keep hook order stable.
	const previousTooltip = useMemo(
		() => ({
			content: (
				<ToolTipContent
					description={intl.formatMessage(messages.previousChange)}
					keymap={previousChangeKeymap}
				/>
			),
		}),
		[intl],
	);
	const nextTooltip = useMemo(
		() => ({
			content: (
				<ToolTipContent
					description={intl.formatMessage(messages.nextChange)}
					keymap={nextChangeKeymap}
				/>
			),
		}),
		[intl],
	);

	// [CCI-18590] Step Through Mode is opt-in via experiment. It only renders when
	// `platform_editor_review_moment_step_through` is enabled — hide the toggle
	// button and the prev/next navigation tray otherwise. Enable-form (default off)
	// so no accidental exposure while the Review moment itself is rolling out, and a
	// future rollout is just a ramp of this experiment. The rest of the review
	// toolbar (view-changes, undo, redo, accept) is unaffected.
	if (!isExperimentEnabled('platform_editor_review_moment_step_through')) {
		return null;
	}

	// [CCI-17296] Step-through is only relevant when there is more than one
	// reviewable change.
	const hasMultipleSegments = totalSegments > 1;
	// Arrows are always enabled when there is more than one segment;
	// navigation wraps around (first <- last, last -> first). Do NOT additionally
	// disable at the ends — `canGoPrevious` / `canGoNext` already encode the
	// wrap-around availability.
	const isPrevDisabled = !canGoPrevious;
	const isNextDisabled = !canGoNext;

	if (!hasMultipleSegments) {
		return null;
	}

	// The navigation and step-through IconButtons opt into tooltips
	// (`isTooltipDisabled` defaults to true); each surfaces its `label` on hover.
	return (
		<div css={actionButtonsCSS}>
			{isStepThroughExpanded && (
				<div css={navigationTrayCSS} data-testid="post-stream-review-navigation">
					<IconButton
						appearance="subtle"
						icon={renderChevronLeftIcon}
						label={intl.formatMessage(messages.previousChange)}
						isTooltipDisabled={false}
						tooltip={previousTooltip}
						isDisabled={isPrevDisabled}
						onClick={onPrevious}
						testId="post-stream-review-prev"
					/>
					<span css={segmentCounterCSS} data-testid="post-stream-review-segment-counter">
						{intl.formatMessage(messages.segmentPosition, {
							current: segmentIndex + 1,
							total: totalSegments,
						})}
					</span>
					<IconButton
						appearance="subtle"
						icon={renderChevronRightIcon}
						label={intl.formatMessage(messages.nextChange)}
						isTooltipDisabled={false}
						tooltip={nextTooltip}
						isDisabled={isNextDisabled}
						onClick={onNext}
						testId="post-stream-review-next"
					/>
				</div>
			)}
			<IconButton
				appearance="subtle"
				icon={renderFullscreenEnterIcon}
				label={intl.formatMessage(messages.stepThrough)}
				isTooltipDisabled={false}
				isSelected={isStepThroughExpanded}
				aria-expanded={isStepThroughExpanded}
				onClick={onToggleStepThrough}
				testId="post-stream-review-step-through"
			/>
		</div>
	);
}
